import type { ExtractedDocumentItem } from './documents';
import type { FirmRoleAssignment } from './firmRole';
import type { Section } from './sections';

export type QuestionnaireStatus =
  | 'in_progress'
  | 'completed'
  | 'opened'
  | 'locked'
  | 'ready_to_start';

export interface EngagementSignature {
  client_time: string | null;
  id: number;
  ip_address: string | null;
  signature: string | null;
  signed_at: string | null;
  user_agent: string | null;
  user_id: number;
  user_name: string;
  section_id: number;
}

export type QuestionType =
  | 'boolean'
  | 'date'
  | 'entries'
  | 'multiple'
  | 'password'
  | 'select'
  | 'text'
  | 'textarea'
  | 'upload'
  | 'email'
  | 'phone';

export interface QuestionNote {
  created_at: string;
  deleted_at?: string | null;
  from_firm_user: boolean;
  id: number;
  questionnaire_question_id: number;
  removed: boolean;
  text: string;
  updated_at?: string | null;
  user_id: number;
}

export interface Question {
  answer: string;
  answer_by: string | null;
  completed: boolean;
  disable_answer_later: boolean;
  disable_not_applicable: boolean;
  document_link?: string;
  follow_up_question_id: number | null;
  uploaded_last_year?: boolean | null;
  formatted_answer: string;
  id: number;
  number_of_characters: string | null;
  optional: boolean;
  options: Array<string>;
  order: number;
  question_type: QuestionType;
  filtered_questionnaire_comments: Array<QuestionNote>;
  section_id: number;
  text: string;
  trigger_for_follow_up_question?: string;
  triggered: boolean;
  prior_year_value?: string;
}

export interface EntityUser {
  id: number;
  full_name: string;
  email: string;
  is_taxpayer: boolean;
  is_spouse: boolean;
}

export interface Questionnaire {
  all_owner_signature_required?: boolean;
  batch_reminder?: boolean | null;
  batch_sending?: boolean | null;
  company_id: number;
  completed: boolean;
  created_at: string;
  sent_at: string | null;
  current_step?: string | null;
  deleted_at?: string | null;
  deposit_amount?: number | null;
  deposit_status?: string | null;
  disclaimer?: string | null;
  document_count_answered: number;
  document_count_total: number;
  document_drop_off_enabled?: boolean;
  document_path?: string | null;
  document_popout_checklist_enabled?: boolean | null;
  documents_from_last_year_as_last_section?: boolean;
  documents_needed: Array<[Array<string>, string, boolean]>;
  documents_uploaded_and_needed?: Array<Question>;
  due_date: string | null;
  earliest_remind_date?: string | null;
  extracted_documents?: Array<ExtractedDocumentItem>;
  firm_custom_status_id?: number | null;
  firm_role_assignments?: Array<FirmRoleAssignment>;
  first_doc_uploaded_at?: string | null;
  formatted_name: string;
  hide_checklist?: boolean;
  id: number;
  in_progress: boolean | null;
  initial_email_message?: string;
  initial_email_subject?: string;
  initial_in_app_message?: string | null;
  initial_sms_message?: string;
  is_draft: boolean;
  is_open_item?: boolean;
  last_activity?: string | null;
  last_activity_at: string | null;
  last_followed_up?: string | null;
  locked: boolean;
  name: string;
  notes: Array<QuestionNote>;
  opened_timestamp: string | null;
  parent_entity_relevant_questionnaire_statuses?: Array<any>;
  processing: boolean;
  questionnaire_questions?: Array<Question>;
  related_documents?: Array<string>;
  relevant_questionnaire_id?: number | null;
  reminder_email_message?: string;
  reminder_email_subject?: string;
  reminder_in_app_message?: string;
  reminder_sms_message?: string;
  reminders_disabled: boolean;
  sections: Array<Section>;
  send_reminder_after_due_date?: boolean;
  status: number | null;
  submitted_at?: string | null;
  tax_year: number;
  track_changes: boolean;
  update_profile_section?: boolean;
  updated_at_by_client?: string | null;
  upload_question_with_document_count_needed: number;
  upload_question_with_document_count_uploaded: number;
  custom_receiver_client_role?: string | null;
}

export const SELECTABLE_CLIENT_ROLES: Array<{
  value: string;
  name: string;
}> = [
  { value: 'use_default', name: 'Use Default (Entity Notification Settings)' },
  { value: 'taxpayer', name: 'Taxpayer' },
  { value: 'primary_contact', name: 'Primary Contact' },
];
